/**
 * Overview media dump — deal uploads, Google Places, Street View, listed website.
 * Empty and vendor-failure states stay honest. No stock photos.
 */
import { useCallback, useState } from 'react'

import { PhotoLightbox } from './PhotoLightbox'
import type { ReactNode } from 'react'
import { ImageOff, Loader2 } from 'lucide-react'
import type { PropertyPhoto } from '@/types/propertyProfile'

/**
 * Why one photo did not load.
 *
 * The lookup that issued these photos reports its own failures in `notes`, but
 * it only speaks for the *search*. A token it issued happily can still fail
 * when the bytes are fetched — an expired token, a key removed since, a
 * refusal from Google — and an `<img>` cannot read the JSON body that says so.
 * The result on a live deal was four broken-image icons and no explanation
 * anywhere on the page.
 *
 * So a failed photo is re-requested once with fetch(), which *can* read the
 * body, and whatever the server said is shown in place of the picture.
 */
async function reasonForFailure(src: string): Promise<string> {
  if (src.startsWith('https://maps.googleapis.com/maps/api/streetview')) {
    // Requested straight from the browser with the browser key. The image
    // endpoint answers a 403 with no CORS headers, so the body is unreadable;
    // the only two reasons it fails are known.
    return 'Street View Static API is not enabled on the browser Maps key, or this site is not on its referrer list.'
  }
  if (src.startsWith('blob:')) {
    return 'This attachment is not an image format the browser can display.'
  }
  if (!src.includes('/property-media/photo')) {
    return 'The image could not be loaded.'
  }
  try {
    // Never the cached copy: the <img> that failed may have cached a reply
    // without CORS headers, and re-reading it would report the cache, not
    // the server.
    const resp = await fetch(src, { credentials: 'omit', cache: 'no-store' })
    if (resp.ok) {
      // It answers now — the earlier failure was transient (a dropped
      // connection, a cancelled navigation), not a broken configuration.
      return 'The image failed once and the server now answers. Reload to retry.'
    }
    const body = await resp.json().catch(() => null)
    const detail = body && typeof body.detail === 'string' ? body.detail : null
    return detail ? `${detail} (HTTP ${resp.status})` : `The server returned HTTP ${resp.status}.`
  } catch {
    return 'The image request never reached the server.'
  }
}

type Props = {
  photos: PropertyPhoto[]
  notes: string[]
  loading: boolean
  formattedAddress: string | null
  photoEditor?: ReactNode
  /** Fill parent height — used on Overview single-screen layout. */
  compact?: boolean
  /** Parent tile supplies chrome — body only. */
  chromeless?: boolean
  /** Dense grid when no photos (fills tile, no void). */
  emptyFallback?: ReactNode
}

export function MediaGallery({
  photos,
  notes,
  loading,
  formattedAddress,
  photoEditor,
  compact = false,
  chromeless = false,
  emptyFallback,
}: Props) {
  const [selected, setSelected] = useState(0)
  const [failures, setFailures] = useState<Record<string, string>>({})
  // The panel is sized to a dossier column; a photo often needs to be read,
  // not just recognised.
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const hero = photos[Math.min(selected, Math.max(photos.length - 1, 0))]
  const thumbs = photos

  const noteFailure = useCallback((src: string) => {
    setFailures((current) => (src in current ? current : { ...current, [src]: 'Checking…' }))
    void reasonForFailure(src).then((reason) => {
      setFailures((current) => ({ ...current, [src]: reason }))
    })
  }, [])

  const heroFailure = hero ? failures[hero.src] : undefined

  const body = (
    <>
      <div className={`relative min-h-0 flex-1 bg-black/40 ${compact ? '' : 'min-h-[240px]'}`}>
        {loading && photos.length === 0 && (
          <div className="flex h-full items-center justify-center text-[11px] text-stone-400">
            <Loader2 size={14} className="mr-2 animate-spin" /> Looking up Places photos…
          </div>
        )}
        {!loading && photos.length === 0 && (
          <div className="absolute inset-0">{emptyFallback ?? <div className="h-full bg-black/50" />}</div>
        )}
        {hero && (
          <>
            {heroFailure ? (
              // No lightbox affordance on a photo that did not load: a
              // cursor-zoom-in over a vendor failure promises something the
              // click cannot deliver.
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/50 px-4 text-center">
                <ImageOff size={16} className="text-stone-600" />
                <p className="text-[10px] text-stone-400">{hero.alt}</p>
                <p className="text-[9px] leading-relaxed text-stone-500">{heroFailure}</p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  aria-label={`Open ${hero.alt} full size`}
                  title="Open full size — arrow keys move between photos"
                  className="absolute inset-0 h-full w-full cursor-zoom-in"
                >
                  <img
                    src={hero.src}
                    alt={hero.alt}
                    onError={() => noteFailure(hero.src)}
                    className="h-full w-full object-cover"
                  />
                </button>
                <span className="pointer-events-none absolute right-1.5 top-1.5 rounded bg-black/60 p-1 text-stone-200">
                  View photo
                </span>
              </>
            )}
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/80 to-transparent px-2 py-1">
              <span className="truncate text-[10px] text-stone-200">{hero.alt}</span>
              {hero.source && (
                <span className="shrink-0 rounded bg-black/60 px-1 py-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-200">
                  {hero.source}
                </span>
              )}
            </div>
          </>
        )}
        {loading && photos.length > 0 && (
          <div className="absolute right-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-stone-300">
            <Loader2 size={10} className="mr-1 inline animate-spin" />
            Fetching
          </div>
        )}
      </div>

      {/* The strip is for choosing between photos. With one, or none, it was a
          row of empty boxes taking a quarter of the panel from the photo (21 Oct). */}
      {thumbs.length > 1 ? (
      <div data-gallery-thumbs className="grid h-11 shrink-0 grid-cols-6 gap-1 border-t border-amber-400/10 p-1">
        {thumbs.slice(0, 6).map((p, i) => (
            <button
              key={`${p.src}-${i}`}
              type="button"
              onClick={() => setSelected(i)}
              title={failures[p.src] ? `${p.alt}: ${failures[p.src]}` : p.alt}
              className={`overflow-hidden rounded border ${
                i === selected ? 'border-amber-300/70' : 'border-transparent opacity-80 hover:opacity-100'
              }`}
            >
              {failures[p.src] ? (
                <span className="flex h-full w-full items-center justify-center bg-black/50">
                  <ImageOff size={11} className="text-stone-600" />
                </span>
              ) : (
                <img
                  src={p.src}
                  alt={p.alt}
                  onError={() => noteFailure(p.src)}
                  className="h-full w-full object-cover"
                />
              )}
            </button>
        ))}
      </div>
      ) : null}
    </>
  )

  const lightbox = lightboxOpen && photos.length > 0 ? (
    <PhotoLightbox
      photos={photos}
      index={Math.min(selected, photos.length - 1)}
      onIndexChange={setSelected}
      onClose={() => setLightboxOpen(false)}
    />
  ) : null

  if (chromeless) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        {body}
        {lightbox}
      </div>
    )
  }

  return (
    <section
      className={`flex flex-col overflow-hidden rounded-lg border border-amber-400/15 bg-black/30 ${
        compact ? 'h-full min-h-0' : 'min-h-[320px]'
      }`}
    >
      <div className="flex h-7 shrink-0 items-center justify-between gap-2 border-b border-amber-400/10 px-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">Photos</span>
        <span className="truncate text-[10px] text-stone-600">
          {photos.length > 0 ? `${photos.length} on this dump` : 'Media dump'}
        </span>
      </div>
      {body}
      {lightbox}
      {(formattedAddress || notes.length > 0 || photoEditor) && !compact && (
        <div className="space-y-1.5 border-t border-amber-400/10 px-3 py-2">
          {formattedAddress && (
            <p className="text-[10px] text-stone-500">Google resolved: {formattedAddress}</p>
          )}
          {notes.map((n) => (
            <p key={n} className="text-[10px] text-stone-500">
              {n}
            </p>
          ))}
          {photoEditor}
        </div>
      )}
    </section>
  )
}

export default MediaGallery
